import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Save, Trash2, CreditCard, Mail, Target, Package2,
  ChevronDown, CheckCircle, Plus, X, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { pageCache } from '../utils/pageCache';
import { useAuth } from '../contexts/AuthContext';
import ShopAddressField from '../components/ShopAddressField';
import { PRO_PRICE_LABEL, FOUNDING_PRICE_LABEL, STRIPE_PORTAL_URL } from '../pricing';
import { fmt, fmtCount, fmtPct } from '../utils/formatters';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia',
  'Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland',
  'Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey',
  'New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina',
  'South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

const BRIEF_HOURS = [5, 6, 7, 8, 9, 10, 11];
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Phoenix', label: 'Arizona' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];
const METRICS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'profit', label: 'Profit' },
  { value: 'transactions', label: 'Orders or items sold' },
  { value: 'units', label: 'Units Sold' },
];
const PERIODS = [
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
];


function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
      <div className="flex items-center gap-2 mb-5">
        <span className="text-[#4A2D5E]">{icon}</span>
        <h3 className="font-serif text-lg text-[#2D2A2E]">{title}</h3>
      </div>
      {children}
    </div>
  );
}


export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();

  // Profile + Business form
  const [form, setForm] = useState({
    name: '',
    business_name: '',
    business_type: '',
    state: '',
    monthly_revenue_range: '',
    cogs_percentage: 30,
  });
  const [saving, setSaving] = useState(false);

  // Daily brief settings
  const [briefEnabled, setBriefEnabled] = useState(true);
  const [briefHour, setBriefHour] = useState(7);
  const [briefTimezone, setBriefTimezone] = useState('America/New_York');
  const [briefEmail, setBriefEmail] = useState('');
  const [savingBrief, setSavingBrief] = useState(false);
  // Weekly digest workbook (attached to the Sunday review)
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestEmail, setDigestEmail] = useState('');
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Goals
  const [goals, setGoals] = useState<any[]>([]);
  const [loadingGoals, setLoadingGoals] = useState(false);

  // Inventory thresholds
  const [productNames, setProductNames] = useState<string[]>([]);
  const [thresholds, setThresholds] = useState<Record<string, string>>({});
  const [savingThresholds, setSavingThresholds] = useState(false);

  // Load user form
  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        business_name: user.business_name || '',
        business_type: user.business_type || '',
        state: user.state || '',
        monthly_revenue_range: user.monthly_revenue_range || '',
        cogs_percentage: user.cogs_percentage || 30,
      });
    }
  }, [user]);

  // Payment status toasts
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      toast.success('Payment successful! Welcome to ClearPath Pro.');
      refreshUser();
    } else if (payment === 'cancelled') {
      toast.error('Payment cancelled.');
    }
  }, [searchParams, refreshUser]);

  // Load settings (daily brief + thresholds)
  useEffect(() => {
    axios.get('/api/settings').then((res) => {
      const s = res.data;
      if (s.brief_enabled !== undefined) setBriefEnabled(s.brief_enabled);
      if (s.brief_hour !== undefined) setBriefHour(s.brief_hour);
      if (s.timezone) setBriefTimezone(s.timezone);
      if (s.brief_email) setBriefEmail(s.brief_email);
      if (s.digest_enabled !== undefined) setDigestEnabled(!!s.digest_enabled);
      if (s.digest_email) setDigestEmail(s.digest_email);
      if (s.inventory_thresholds) {
        const mapped: Record<string, string> = {};
        Object.entries(s.inventory_thresholds).forEach(([k, v]) => {
          mapped[k] = String(v);
        });
        setThresholds(mapped);
      }
    }).catch(() => {});
  }, []);

  // Load goals
  useEffect(() => {
    setLoadingGoals(true);
    axios.get('/api/goals')
      .then((res) => setGoals(res.data.goals || []))
      .catch(() => setGoals([]))
      .finally(() => setLoadingGoals(false));
  }, []);

  // Load products for threshold inputs
  useEffect(() => {
    axios.get('/api/products?period=month').then((res) => {
      const names = (res.data.products || []).map((p: any) => p.name);
      setProductNames(names);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings', form);
      await refreshUser();
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBrief = async () => {
    setSavingBrief(true);
    try {
      await axios.put('/api/settings', {
        brief_enabled: briefEnabled,
        brief_hour: briefHour,
        timezone: briefTimezone,
        brief_email: briefEmail || user?.email,
        digest_enabled: digestEnabled,
        digest_email: digestEmail, // "" clears it — the validator maps blank to null
      });
      toast.success('Daily brief settings saved');
    } catch {
      toast.error('Failed to save daily brief settings');
    } finally {
      setSavingBrief(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    setReportError(null);
    try {
      const res = await axios.get('/api/digest/workbook', { responseType: 'blob' });
      const cd: string = res.headers['content-disposition'] || '';
      const name = /filename="([^"]+)"/.exec(cd)?.[1] || 'ClearPath_WeeklyReport.xlsx';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      let msg = 'Could not build the report.';
      if (e?.response?.data instanceof Blob) {
        try {
          msg = JSON.parse(await e.response.data.text()).detail || msg;
        } catch {
          /* keep the default message */
        }
      }
      setReportError(msg);
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      await axios.delete(`/api/goals/${goalId}`);
      setGoals((prev) => prev.filter((g) => g.goal_id !== goalId));
      toast.success('Goal removed');
    } catch {
      toast.error('Failed to delete goal');
    }
  };

  const handleSaveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const parsed: Record<string, number> = {};
      Object.entries(thresholds).forEach(([k, v]) => {
        const n = parseInt(v, 10);
        if (!isNaN(n) && n > 0) parsed[k] = n;
      });
      await axios.put('/api/settings', { inventory_thresholds: parsed });
      toast.success('Thresholds saved');
    } catch {
      toast.error('Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  const handleDeleteData = async () => {
    if (!window.confirm('Are you sure? This will delete ALL your transaction data and AI insights. This cannot be undone.')) return;
    try {
      await axios.delete('/api/settings/data');
      pageCache.clear('/api/'); // cached screens showed the deleted data's results
      toast.success('All data deleted');
    } catch {
      toast.error('Failed to delete data');
    }
  };

  const [discountCode, setDiscountCode] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountValid, setDiscountValid] = useState<boolean | null>(null);
  const [discountType, setDiscountType] = useState<string | null>(null);
  const [discountPercent, setDiscountPercent] = useState<number | null>(null);
  const [discountMonths, setDiscountMonths] = useState<number>(1);
  const [discountChecking, setDiscountChecking] = useState(false);

  const handleCheckDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountChecking(true);
    try {
      const res = await axios.post('/api/discount/validate', { code: discountCode.trim() });
      const type: string = res.data?.type ?? 'first_month_free';
      const pct: number | null = typeof res.data?.discount_percent === 'number' ? res.data.discount_percent : null;
      const months: number = typeof res.data?.discount_months === 'number' && res.data.discount_months > 1 ? res.data.discount_months : 1;
      setDiscountValid(true);
      setDiscountType(type);
      setDiscountPercent(pct);
      setDiscountMonths(months);
      toast.success(
        type === 'founding_rate'
          ? `Founding rate applied: ${FOUNDING_PRICE_LABEL}/mo, locked for life.`
          : type === 'percent_off' && pct
            ? `Discount code applied! ${fmtPct(pct)} off ${months > 1 ? `for ${months} months` : 'your first month'}.`
            : 'Discount code applied! First month free.'
      );
    } catch (err: any) {
      setDiscountValid(false);
      setDiscountType(null);
      setDiscountPercent(null);
      setDiscountMonths(1);
      toast.error(err.response?.data?.detail || 'Invalid discount code');
    } finally {
      setDiscountChecking(false);
    }
  };

  // A code entered during onboarding rides on the user until they upgrade.
  // Prefill it and validate once so the button already shows the right price.
  const pendingCode: string | undefined = (user as any)?.pending_code;
  useEffect(() => {
    if (!pendingCode || discountCode) return;
    setDiscountCode(pendingCode);
    setShowDiscount(true);
    axios.post('/api/discount/validate', { code: pendingCode })
      .then((res) => {
        const type: string = res.data?.type ?? 'first_month_free';
        setDiscountValid(true);
        setDiscountType(type);
        setDiscountPercent(typeof res.data?.discount_percent === 'number' ? res.data.discount_percent : null);
        setDiscountMonths(typeof res.data?.discount_months === 'number' && res.data.discount_months > 1 ? res.data.discount_months : 1);
      })
      .catch(() => { /* spent or expired since onboarding — leave it for them to retype */ });
    // Deliberately keyed on pendingCode alone: this is a one-time prefill, and
    // adding discountCode would re-run it every keystroke.
  }, [pendingCode]); // eslint-disable-line

  const isFounding = discountValid === true && discountType === 'founding_rate';
  const isPercentOff = discountValid === true && discountType === 'percent_off' && !!discountPercent;
  const upgradePriceLabel = isFounding ? FOUNDING_PRICE_LABEL : PRO_PRICE_LABEL;
  const appliedCodeMessage = isFounding
    ? `Founding rate: ${FOUNDING_PRICE_LABEL}/mo for as long as you stay. Click upgrade to continue.`
    : isPercentOff
      ? `${fmtPct(discountPercent)} off ${discountMonths > 1 ? `for ${discountMonths} months` : 'your first month'}, then ${PRO_PRICE_LABEL}/mo. Click upgrade to continue.`
      : 'First month free! Click upgrade to continue.';

  const handleUpgrade = async () => {
    try {
      const endpoint = discountValid ? '/api/stripe/create-checkout-with-discount' : '/api/stripe/create-checkout';
      const payload = discountValid ? { discount_code: discountCode.trim() } : {};
      const res = await axios.post(endpoint, payload);
      window.location.href = res.data.url;
    } catch {
      toast.error('Failed to start checkout');
    }
  };

  const metricLabel = (m: string) => METRICS.find((x) => x.value === m)?.label ?? m;
  const periodLabel = (p: string) => PERIODS.find((x) => x.value === p)?.label ?? p;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-[#2D2A2E]">Settings</h1>
      </div>

      <div className="max-w-2xl space-y-8">
        {/* Profile */}
        <SectionCard title="Profile" icon={null}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Email</label>
              <input value={user?.email || ''} disabled className="w-full bg-[#FAF9F7] border border-[#E6E2DE] rounded-[8px] h-11 font-sans text-sm px-3 text-[#6B6670] cursor-not-allowed" />
            </div>
          </div>
        </SectionCard>

        {/* Business */}
        <SectionCard title="Business" icon={null}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Business Name</label>
              <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Business Type</label>
              <select value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all">
                <option value="">Select...</option>
                {['Coffee Shop', 'Restaurant', 'Retail Store', 'Bakery', 'Food Truck', 'Bar', 'Other'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">State</label>
              <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all">
                <option value="">Select...</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <ShopAddressField key={user?.user_id ?? 'none'} initial={user?.location ?? null} />
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Revenue Range</label>
              <select value={form.monthly_revenue_range} onChange={(e) => setForm({ ...form, monthly_revenue_range: e.target.value })} className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all">
                <option value="">Select...</option>
                {['Under $10K', '$10K–$50K', '$50K–$100K', '$100K–$500K', '$500K+'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-2">
                COGS Percentage: <span className="text-[#4A2D5E] tabular-nums">{form.cogs_percentage}%</span>
              </label>
              <input type="range" min={0} max={100} value={form.cogs_percentage} onChange={(e) => setForm({ ...form, cogs_percentage: Number(e.target.value) })} className="w-full accent-[#4A2D5E]" />
            </div>
          </div>
          <button onClick={handleSave} disabled={saving} className="mt-6 flex items-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors disabled:opacity-50">
            <Save size={16} strokeWidth={1.5} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </SectionCard>

        {/* Daily Brief */}
        <SectionCard title="Daily Brief" icon={<Mail size={18} strokeWidth={1.5} />}>
          <p className="font-sans text-sm text-[#6B6670] mb-5">
            Your morning email with the top moves for your business — delivered before you open, so you know exactly where to focus.
          </p>

          {/* Enable toggle */}
          <div className="flex items-center justify-between mb-5">
            <span className="font-sans text-sm font-medium text-[#2D2A2E]">Enable daily brief</span>
            <button
              onClick={() => setBriefEnabled(!briefEnabled)}
              className={`relative w-11 h-6 rounded-full transition-colors ${briefEnabled ? 'bg-[#2E6B4F]' : 'bg-[#E6E2DE]'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${briefEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {briefEnabled && (
            <div className="space-y-4 mb-5">
              <div>
                <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Send at</label>
                <select
                  value={briefHour}
                  onChange={(e) => setBriefHour(Number(e.target.value))}
                  className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none"
                >
                  {BRIEF_HOURS.map((h) => <option key={h} value={h}>{h}:00 am</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Timezone</label>
                <select
                  value={briefTimezone}
                  onChange={(e) => setBriefTimezone(e.target.value)}
                  className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none"
                >
                  {TIMEZONES.map((tz) => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Send to</label>
                <input
                  type="email"
                  value={briefEmail}
                  onChange={(e) => setBriefEmail(e.target.value)}
                  placeholder={user?.email || 'your@email.com'}
                  className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none"
                />
              </div>
            </div>
          )}

          {/* Weekly digest workbook */}
          <div className="border-t border-[#E6E2DE] pt-5 mb-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-sans text-sm font-medium text-[#2D2A2E]">Attach the Excel weekly report to my Sunday review</div>
                <div className="font-sans text-xs text-[#6B6670] mt-0.5">A five-tab workbook — dashboard, hourly traffic, item ranking, 13-week archive.</div>
              </div>
              <button
                onClick={() => setDigestEnabled(!digestEnabled)}
                className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${digestEnabled ? 'bg-[#2E6B4F]' : 'bg-[#E6E2DE]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${digestEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
            {digestEnabled && (
              <div className="mt-4">
                <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-1.5">Also send the report to (optional)</label>
                <input
                  type="email"
                  value={digestEmail}
                  onChange={(e) => setDigestEmail(e.target.value)}
                  placeholder="bookkeeper@example.com"
                  className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleSaveBrief}
              disabled={savingBrief}
              className="flex items-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
            >
              <Save size={14} strokeWidth={1.5} />
              {savingBrief ? 'Saving...' : 'Save Brief Settings'}
            </button>
            <button
              onClick={handleDownloadReport}
              disabled={downloadingReport}
              className="flex items-center gap-2 bg-white text-[#4A2D5E] border border-[#4A2D5E]/30 hover:bg-[#4A2D5E]/5 rounded-[8px] font-sans font-medium px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
            >
              <Download size={14} strokeWidth={1.5} />
              {downloadingReport ? 'Building...' : "Download this week's report"}
            </button>
          </div>
          {reportError && (
            <p className="font-sans text-xs text-[#B54A4A] mt-3">{reportError}</p>
          )}
        </SectionCard>

        {/* Goals */}
        <SectionCard title="Goals" icon={<Target size={18} strokeWidth={1.5} />}>
          <p className="font-sans text-sm text-[#6B6670] mb-5">
            Goals are created from Sales › Overview. Manage or remove them here.
          </p>

          {loadingGoals ? (
            <div className="space-y-2">
              {[1, 2].map((i) => <div key={i} className="animate-pulse h-14 bg-[#F2F0ED] rounded-[10px]" />)}
            </div>
          ) : goals.length === 0 ? (
            <div className="flex flex-col items-center py-8 gap-2">
              <Target size={32} strokeWidth={1} className="text-[#4A2D5E]/20" />
              <p className="font-sans text-sm text-[#6B6670]">No goals yet. Add one from Sales › Overview.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {goals.map((g) => (
                <div key={g.goal_id} className="flex items-center justify-between bg-[#FAF9F7] rounded-[10px] px-4 py-3">
                  <div>
                    <div className="font-sans text-sm font-medium text-[#2D2A2E]">{g.label}</div>
                    <div className="font-sans text-xs text-[#6B6670] mt-0.5">
                      {metricLabel(g.metric)} · {periodLabel(g.period)} · Target: {
                        g.metric === 'revenue' || g.metric === 'profit'
                          ? fmt(g.target)
                          : fmtCount(g.target)
                      }
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteGoal(g.goal_id)}
                    className="p-1.5 text-[#6B6670] hover:text-[#B54A4A] transition-colors rounded"
                    title="Delete goal"
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Inventory Thresholds */}
        <SectionCard title="Inventory Thresholds" icon={<Package2 size={18} strokeWidth={1.5} />}>
          <p className="font-sans text-sm text-[#6B6670] mb-5">
            Set minimum stock warning levels per product. When a Hot-selling product exceeds this many units sold, you'll see a "Monitor Stock" badge on the Products page.
          </p>

          {productNames.length === 0 ? (
            <p className="font-sans text-sm text-[#6B6670] text-center py-6">
              Upload transaction data to configure per-product thresholds.
            </p>
          ) : (
            <>
              <div className="space-y-3 mb-5">
                {productNames.map((name) => (
                  <div key={name} className="flex items-center gap-3">
                    <span className="font-sans text-sm text-[#2D2A2E] flex-1 truncate">{name}</span>
                    <input
                      type="number"
                      min={0}
                      placeholder="e.g. 50"
                      value={thresholds[name] ?? ''}
                      onChange={(e) => setThresholds((prev) => ({ ...prev, [name]: e.target.value }))}
                      className="w-24 bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 font-sans text-sm px-3 outline-none text-right tabular-nums"
                    />
                    {thresholds[name] && (
                      <button
                        onClick={() => setThresholds((prev) => { const n = { ...prev }; delete n[name]; return n; })}
                        className="text-[#6B6670] hover:text-[#B54A4A] transition-colors"
                        title="Clear threshold"
                      >
                        <X size={14} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={handleSaveThresholds}
                disabled={savingThresholds}
                className="flex items-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
              >
                <Save size={14} strokeWidth={1.5} />
                {savingThresholds ? 'Saving...' : 'Save Thresholds'}
              </button>
            </>
          )}
        </SectionCard>

        {/* Plan */}
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
          <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Plan</h3>
          <div className="flex items-center gap-3 mb-4">
            {user?.subscription_status === 'cancelled' ? (
              <span className="text-sm font-sans px-3 py-1 rounded-full bg-[#B54A4A]/10 text-[#B54A4A] border border-[#B54A4A]/20">
                Cancelled
              </span>
            ) : (
              <span className={`text-sm font-sans px-3 py-1 rounded-full ${
                user?.plan === 'pro' ? 'bg-[#2E6B4F] text-white' : 'bg-[#F0EBF5] text-[#4A2D5E]'
              }`}>
                {user?.plan === 'pro' ? `Pro Plan — ${PRO_PRICE_LABEL}/mo` : 'Free Plan'}
              </span>
            )}
          </div>
          {user?.plan === 'pro' && user?.subscription_status !== 'cancelled' && (
            <a
              href={STRIPE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-sans text-sm text-[#4A2D5E] hover:text-[#3D254D] underline underline-offset-2"
            >
              <CreditCard size={14} strokeWidth={1.5} />
              Manage billing — update your card, download invoices, or cancel
            </a>
          )}
          {(user?.plan !== 'pro' || user?.subscription_status === 'cancelled') && (
            <div className="space-y-4">
              <button onClick={handleUpgrade} className="flex items-center gap-2 bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors">
                <CreditCard size={16} strokeWidth={1.5} />
                {user?.subscription_status === 'cancelled' ? `Reactivate Pro — ${upgradePriceLabel}/mo` : `Upgrade to Pro — ${upgradePriceLabel}/mo`}
              </button>
              {/* Discount code */}
              <div>
                <button
                  onClick={() => setShowDiscount((v) => !v)}
                  className="font-sans text-xs text-[#6B6670] hover:text-[#4A2D5E] transition-colors"
                >
                  Have a discount code?
                </button>
                {showDiscount && (
                  <div className="flex gap-2 mt-2">
                    <input
                      value={discountCode}
                      onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountValid(null); setDiscountType(null); setDiscountPercent(null); }}
                      placeholder="Enter code"
                      className={`flex-1 border rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] ${
                        discountValid === true ? 'border-[#2E6B4F] bg-[#2E6B4F]/5' : discountValid === false ? 'border-[#B54A4A]' : 'border-[#E6E2DE]'
                      }`}
                    />
                    <button
                      onClick={handleCheckDiscount}
                      disabled={discountChecking || !discountCode.trim()}
                      className="px-4 bg-[#4A2D5E] text-white rounded-[8px] font-sans text-sm font-medium hover:bg-[#3D254D] disabled:opacity-50 transition-colors"
                    >
                      {discountChecking ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                {discountValid === true && (
                  <p className="font-sans text-xs text-[#2E6B4F] mt-1 flex items-center gap-1">
                    <CheckCircle size={12} strokeWidth={2} />
                    {appliedCodeMessage}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Danger zone */}
        <div className="bg-white border border-[#B54A4A]/20 rounded-[14px] p-6">
          <h3 className="font-serif text-lg text-[#B54A4A] mb-2">Danger Zone</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-4">Delete all your transaction data, upload history, and AI insights. This cannot be undone.</p>
          <button onClick={handleDeleteData} className="flex items-center gap-2 bg-white text-[#B54A4A] border border-[#B54A4A]/30 hover:bg-[#B54A4A]/5 rounded-[8px] font-sans font-medium px-6 py-2.5 transition-colors">
            <Trash2 size={16} strokeWidth={1.5} />
            Delete All Data
          </button>
        </div>
      </div>
    </motion.div>
  );
}
